import { css } from '@emotion/css';

import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { FormModal } from '@/components/modals/FormModal/FormModal';
import { FormModalActions } from '@/components/modals/FormModal/FormModalActions';
import {
  FormProvider,
  useForm,
  useFormContext,
  useSubmitSuccessHandler,
} from '@/components/react-hook-form';
import { useFormSaveHandler } from '@/hooks/useFormSaveHandler';

// The base Modal pins its paper to 80vh, which dwarfs this single-field form.
const fitContentModalStyle = css({
  height: 'auto',
});

interface EdgeLabelEditFormShape {
  label: string;
}

type LabelEditModalInnerProps = Omit<LabelEditModalProps, 'initialLabel'>;

function LabelEditModalInner({
  onClose,
  onSave,
  placeholder = 'Describe this relationship',
  helpText = 'Leave blank to remove the label and show a plain line.',
  required,
}: LabelEditModalInnerProps) {
  const { formRef, handleSave } = useFormSaveHandler();
  const { control, formState, handleSubmit } =
    useFormContext<EdgeLabelEditFormShape>();

  const onSubmit = handleSubmit((formData) => {
    onSave(formData.label);
    return Promise.resolve();
  });

  useSubmitSuccessHandler(onClose);

  return (
    <FormModal
      isOpen
      onClose={onClose}
      heading="Edit label"
      rightHeaderContent={null}
      classes={{ paper: fitContentModalStyle }}
      data-testid="label-edit-modal"
      actions={
        <FormModalActions.Provider formState={formState}>
          <FormModalActions.CancelButton onClick={onClose} />
          <FormModalActions.SaveButton onClick={handleSave}>
            Save changes
          </FormModalActions.SaveButton>
        </FormModalActions.Provider>
      }
    >
      <form ref={formRef} onSubmit={onSubmit} noValidate>
        <FormLayoutRow>
          <FormLayoutItem>
            <FormAwareTextInput<EdgeLabelEditFormShape>
              control={control}
              fieldName="label"
              label="Label"
              placeholder={placeholder}
              helpText={helpText}
              required={required}
              autoFocus
              multiline
              maxRows={3}
            />
          </FormLayoutItem>
        </FormLayoutRow>
      </form>
    </FormModal>
  );
}

export interface LabelEditModalProps {
  initialLabel: string;
  onClose: () => void;
  onSave: (label: string) => void;
  /** The field placeholder. The default describes a connection label. */
  placeholder?: string;
  /** The field help text. The default describes a connection label. */
  helpText?: string;
  /** Blocks a save of blank text. By default, the label can be blank. */
  required?: boolean;
}

/**
 * Edits the text of one diagram label, such as the label of a connection or
 * of a phase band. `onSave` gets the text as the user entered it, which can
 * be blank unless `required` is set.
 */
export function LabelEditModal({
  initialLabel,
  ...props
}: LabelEditModalProps) {
  const formMethods = useForm<EdgeLabelEditFormShape>({
    defaultValues: { label: initialLabel },
  });

  return (
    <FormProvider {...formMethods}>
      <LabelEditModalInner {...props} />
    </FormProvider>
  );
}
